import {
  Flex,
  Box,
  Text,
  useToast,
  Divider,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createCandidate, fetchVacancyList } from '../../../api/recruitment/recruitment'
import handleToast from '../../HandleToast'
import ModalInput from '../../forms/modalForms/modalInput'
import ModalSelect from '../../forms/modalForms/modalSelect'
import ModalFileInput from '../../forms/modalForms/modalFileInput'
import ModalPhoneInput from '../../forms/modalForms/modalPhoneInput'
import FormModal from '../../common/FormModal'
import { countrySelectOptions } from '../../countries/PhoneDial'
import axios from 'axios'

interface AddCandidateModalProps {
  isOpen: boolean
  onClose: () => void
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text textStyle="h-xs" color="solid.700" mb="2px">
    {children}
  </Text>
)

const AddCandidateModal: React.FC<AddCandidateModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    source: '',
    notes: '',
    vacancy: '',
  })
  const [resumeFile, setResumeFile] = useState<File | null>(null)

  const { data: vacanciesData, isLoading: vacanciesLoading } = useQuery({
    queryKey: ['vacancies-select'],
    queryFn: () => fetchVacancyList({ search: '', page: 1, pageSize: 200, filters: {} }),
  })

  const vacancies = Array.isArray(vacanciesData?.data) ? vacanciesData.data : []

  const mutation = useMutation({
    mutationFn: createCandidate,
    onSuccess: () => {
      handleToast(
        toast,
        true,
        t('recruitment.modals.addCandidate.successTitle'),
        t('recruitment.modals.addCandidate.successMessage')
      )
      queryClient.invalidateQueries({ queryKey: ['candidates'] })
      queryClient.invalidateQueries({ queryKey: ['applications'] })
      queryClient.invalidateQueries({ queryKey: ['vacancy-overview'] })
      handleClose()
    },
    onError: (error: unknown) => {
      let message = t('recruitment.modals.addCandidate.errorMessage')

      if (axios.isAxiosError(error)) {
        const data = error.response?.data

        if (typeof data === 'string') {
          message = data
        } else if (data?.detail) {
          message = data.detail
        } else if (typeof data === 'object' && data !== null) {
          const firstError = Object.entries(data)[0]

          if (firstError) {
            const [field, value] = firstError
            if (Array.isArray(value)) {
              message = `${field}: ${value[0]}`
            } else {
              message = `${field}: ${String(value)}`
            }
          }
        }
      } else if (error instanceof Error) {
        message = error.message
      }

      handleToast(
        toast,
        false,
        t('recruitment.modals.addCandidate.errorTitle'),
        message
      )
    },
  })

  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      country: '',
      source: '',
      notes: '',
      vacancy: '',
    })
    setResumeFile(null)
    onClose()
  }

  const handleSubmit = () => {
    if (!formData.first_name || !formData.last_name) return

    const payload = new FormData()

    if (resumeFile) {
      payload.append('resume', resumeFile, resumeFile.name)
    }

    payload.append('first_name', formData.first_name)
    payload.append('last_name', formData.last_name)

    if (formData.email) payload.append('email', formData.email)
    if (formData.phone) payload.append('phone', formData.phone)
    if (formData.country) payload.append('country', formData.country)
    if (formData.source) payload.append('source', formData.source)
    if (formData.notes) payload.append('notes', formData.notes)
    if (formData.vacancy) payload.append('vacancy', formData.vacancy)

    mutation.mutate(payload)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const sourceOptions = [
    { value: 'linkedin', label: t('recruitment.modals.addCandidate.sources.linkedin') },
    { value: 'referral', label: t('recruitment.modals.addCandidate.sources.referral') },
    { value: 'website', label: t('recruitment.modals.addCandidate.sources.website') },
    { value: 'job_board', label: t('recruitment.modals.addCandidate.sources.jobBoard') },
    { value: 'agency', label: t('recruitment.modals.addCandidate.sources.agency') },
    { value: 'other', label: t('recruitment.modals.addCandidate.sources.other') },
  ]

  const vacancyOptions = vacancies.map((v: { id: string; title: string; country: string }) => ({
    value: v.id,
    label: v.title + (v.country ? ` (${v.country})` : ''),
  }))

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('recruitment.modals.addCandidate.title')}
      onSubmit={handleSubmit}
      isLoading={mutation.isPending}
      submitLabel={t('recruitment.modals.addCandidate.addButton')}
    >
      {/* ── Personal info ── */}
      <SectionLabel>Personal Information</SectionLabel>

      <Flex gap="16px" width="100%">
        <ModalInput
          label={t('common.form.firstName')}
          type="text"
          name="first_name"
          placeholder={t('common.form.firstNamePlaceholder')}
          value={formData.first_name}
          onChange={handleChange}
          required
        />
        <ModalInput
          label={t('common.form.lastName')}
          type="text"
          name="last_name"
          placeholder={t('common.form.lastNamePlaceholder')}
          value={formData.last_name}
          onChange={handleChange}
          required
        />
      </Flex>

      <Flex gap="16px" width="100%">
        <ModalInput
          label={t('common.form.email')}
          type="email"
          name="email"
          placeholder={t('common.form.emailPlaceholder')}
          value={formData.email}
          onChange={handleChange}
        />
        <ModalSelect
          label={t('workforce.people.countryOfWork')}
          name="country"
          value={formData.country}
          options={countrySelectOptions}
          onChange={handleChange}
          placeholder={t('common.placeholders.selectCountry')}
          showCountryFlag
        />
      </Flex>

      <ModalPhoneInput
        label={t('common.form.phone')}
        value={formData.phone}
        onChange={(phone) => setFormData((prev) => ({ ...prev, phone }))}
      />

      <Divider borderColor="solid.50" />

      {/* ── Application details ── */}
      <SectionLabel>Application Details</SectionLabel>

      <Flex gap="16px" width="100%">
        <Box flex="1">
          <ModalSelect
            label="Vacancy"
            name="vacancy"
            value={formData.vacancy}
            options={vacancyOptions}
            onChange={handleChange}
            placeholder={
              vacanciesLoading
                ? 'Loading...'
                : vacancies.length === 0
                  ? 'No vacancies'
                  : 'Select vacancy'
            }
            searchDisabled={vacancies.length < 8}
            isLoading={vacanciesLoading}
          />
        </Box>
        <Box flex="1">
          <ModalSelect
            label={t('recruitment.modals.addCandidate.source')}
            name="source"
            value={formData.source}
            options={sourceOptions}
            onChange={handleChange}
            placeholder={t('recruitment.modals.addCandidate.selectSource')}
            searchDisabled
          />
        </Box>
      </Flex>

      <ModalFileInput
        label="Resume"
        accept=".pdf,application/pdf"
        maxSizeMB={5}
        detail="PDF only, max 5 MB"
        value={resumeFile}
        onChange={setResumeFile}
      />

      <ModalInput
        label={t('recruitment.modals.addCandidate.notes')}
        type="textarea"
        name="notes"
        height="80px"
        placeholder={t('recruitment.modals.addCandidate.notesPlaceholder')}
        value={formData.notes}
        onChange={handleChange}
      />
    </FormModal>
  )
}

export default AddCandidateModal
